import { MessageCircleXIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  LanguageAnalysisShape,
  languageAnalysisType,
  comparisonOperators,
  ComparisonOperator,
  LanguageAnalysisProps,
  languageAnalysisCategories,
  LanguageAnalysisCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isLanguageAnalysisShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is LanguageAnalysisShape {
  return shape.type === languageAnalysisType;
}

export class LanguageAnalysisTool extends ToolStateNode {
  static override id = languageAnalysisType;

  static override uiToolDef = (editor: Editor) => ({
    id: languageAnalysisType,
    icon: MessageCircleXIcon,
    label: LanguageAnalysisShapeUtil.docs.title,
    defaultColor: LanguageAnalysisShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(languageAnalysisType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={LanguageAnalysis}
        util={LanguageAnalysisShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: languageAnalysisType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class LanguageAnalysisShapeUtil extends BindableUtil<LanguageAnalysisShape> {
  static override migrations = createMigrations(languageAnalysisType);
  static override type = languageAnalysisType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Language Analysis",
    description:
      "Use AI to analyze the language of the content. This is for when the post itself doesn't have a language tag.",
  };

  override getType() {
    return languageAnalysisType;
  }

  static override props: RecordProps<LanguageAnalysisShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    languageAnalysisCategory: T.literalEnum(...languageAnalysisCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      languageAnalysisCategory: "English" as const,
      operator: ">=" as const,
      value: 0.9,
      metadata: undefined,
    };
  }

  override component(shape: LanguageAnalysisShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <LanguageAnalysis
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function LanguageAnalysis({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: LanguageAnalysisShape;
  className?: string;
  updateShape: (
    shape: LanguageAnalysisShape,
    props: Partial<LanguageAnalysisProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          LanguageAnalysisShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={LanguageAnalysisShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Language Analysis Category"
            options={languageAnalysisCategories}
            value={shape.props.languageAnalysisCategory}
            className="w-[160px] shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                languageAnalysisCategory: v as LanguageAnalysisCategory,
              })
            }
            tooltip={LanguageAnalysisShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
